import React, { useState, useEffect } from 'react';
import { featureFlagsApi } from '../services/api';
import { 
  Card,
  CardHeader,
  CardBody,
  LoadingSpinner,
  ErrorMessage
} from './common';
import { Flag, ToggleRight, ToggleLeft, Users, Building } from 'lucide-react';

const FeatureFlagStats = ({ userId = null, buildingId = null }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
  }, [userId, buildingId]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // If specific user or building is provided, fetch specific stats
      if (userId) {
        const response = await featureFlagsApi.getUserFeatureDetails(userId);
        const userFlags = response.data || [];
        setStats({
          totalFeatureFlags: userFlags.length,
          enabledFeatureFlags: userFlags.filter(f => f.isEnabled).length,
          disabledFeatureFlags: userFlags.filter(f => !f.isEnabled).length,
          uniqueUsers: 1,
          uniqueBuildings: new Set(userFlags.filter(f => f.buildingId).map(f => f.buildingId)).size
        });
      } else if (buildingId) {
        const response = await featureFlagsApi.getBuildingFeatures(buildingId);
        const buildingFlags = response.data || [];
        setStats({
          totalFeatureFlags: buildingFlags.length,
          enabledFeatureFlags: buildingFlags.filter(f => f.isEnabled).length,
          disabledFeatureFlags: buildingFlags.filter(f => !f.isEnabled).length,
          uniqueUsers: new Set(buildingFlags.map(f => f.userId)).size,
          uniqueBuildings: 1
        });
      } else {
        // Fetch global stats
        const response = await featureFlagsApi.getStats();
        setStats(response.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch feature flag statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardBody>
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        </CardBody>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardBody>
          <ErrorMessage message={error} />
        </CardBody>
      </Card>
    );
  }

  const getTitle = () => {
    if (userId) return 'User Feature Flags';
    if (buildingId) return 'Building Feature Flags';
    return 'Feature Flag Statistics';
  };

  const statCards = [
    {
      title: 'Total Flags',
      value: stats?.totalFeatureFlags || 0,
      icon: Flag,
      color: 'bg-blue-500',
      textColor: 'text-blue-600'
    },
    {
      title: 'Enabled',
      value: stats?.enabledFeatureFlags || 0,
      icon: ToggleRight,
      color: 'bg-green-500',
      textColor: 'text-green-600'
    },
    {
      title: 'Disabled',
      value: stats?.disabledFeatureFlags || 0,
      icon: ToggleLeft,
      color: 'bg-gray-500',
      textColor: 'text-gray-600'
    },
    {
      title: 'Users',
      value: stats?.uniqueUsers || 0,
      icon: Users,
      color: 'bg-primary-500',
      textColor: 'text-primary-600'
    },
    {
      title: 'Buildings',
      value: stats?.uniqueBuildings || 0,
      icon: Building,
      color: 'bg-purple-500',
      textColor: 'text-purple-600'
    }
  ];

  return (
    <Card>
      <CardHeader>
        <h3 className="text-lg font-semibold text-gray-900">{getTitle()}</h3>
      </CardHeader>
      <CardBody>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {statCards.map((stat, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center">
                <div className={`${stat.color} p-2 rounded-lg`}>
                  <stat.icon size={20} className="text-white" />
                </div>
                <div className="ml-3">
                  <div className="text-2xl font-bold text-gray-900">
                    {stat.value.toLocaleString()}
                  </div>
                  <div className={`text-sm font-medium ${stat.textColor}`}>
                    {stat.title}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {stats && (stats.totalFeatureFlags > 0) && (
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-500 mb-2">Enabled Rate</div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full" 
                    style={{
                      width: `${(stats.enabledFeatureFlags / stats.totalFeatureFlags) * 100}%`
                    }}
                  ></div>
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  {Math.round((stats.enabledFeatureFlags / stats.totalFeatureFlags) * 100)}% enabled
                </div>
              </div>
              
              <div>
                <div className="text-sm text-gray-500 mb-2">Coverage</div>
                <div className="space-y-1">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">{stats.uniqueUsers}</span> users affected
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">{stats.uniqueBuildings}</span> buildings involved
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default FeatureFlagStats;